/**
 * Environment and device detection utilities.
 * These functions help determine the environment in which the app is running
 * and provide consistent device detection across the application.
 */

/**
 * Determines if the application is being accessed via an IP address
 * rather than localhost or a domain name. This is particularly useful
 * for identifying mobile access on a local network.
 * 
 * @returns {boolean} True if accessed via IP address, false otherwise
 */
export function isAccessingViaIpAddress() {
  if (typeof window === 'undefined') return false;
  
  return Boolean(
    window.location.hostname.match(/^\d+\.\d+\.\d+\.\d+$/) &&
    !window.location.hostname.includes('localhost')
  );
}

/**
 * Gets the current hostname (for API URL construction)
 * 
 * @returns {string} Current hostname
 */
export function getCurrentHostname() {
  if (typeof window === 'undefined') return '127.0.0.1';
  return window.location.hostname;
}

/**
 * Determines if we're running in a mobile debug environment,
 * which is automatically detected via IP address access pattern.
 * 
 * @returns {boolean} True if in mobile debug environment
 */
export function isMobileDebugEnvironment() {
  return isAccessingViaIpAddress();
}

/**
 * Gets the appropriate API base URL based on the current environment.
 * This handles mobile access patterns to ensure that API requests work
 * when accessing from mobile devices on a local network.
 * 
 * @param {string} [defaultPort='5000'] - The default backend port
 * @returns {string} The appropriate API base URL
 */
export function getApiBaseUrl(defaultPort = '5000') {
  // If explicit URL is set in env, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Check if accessing via IP address (likely mobile device on local network)
  if (isAccessingViaIpAddress()) {
    const hostname = getCurrentHostname();
    return `http://${hostname}:${defaultPort}/api`;
  }
  
  // Default localhost for desktop development
  return "http://127.0.0.1:5000/api";
}
